import React, { useState } from 'react';
import { Wallet, ChevronDown, ArrowRight, Loader } from 'lucide-react';
import { btn } from '../../utils/buttonStyles';

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-violet-500', 'bg-emerald-500',
  'bg-orange-500', 'bg-pink-500', 'bg-teal-500',
];

const getInitials = (firstName, lastName) =>
  ((firstName?.[0] ?? '') + (lastName?.[0] ?? '')).toUpperCase() || '?';

const MemberRow = ({ member, idx, currentUserId, expandedMembers, onToggleMember, onSettleUp, theme }) => {
  const isMe      = String(member.userId) === currentUserId;
  const owesTo    = member.owesTo || [];
  const expanded  = expandedMembers[member.userId];
  const canExpand = owesTo.length > 0;

  return (
    <div className="overflow-hidden">
      <button
        type="button"
        onClick={() => canExpand && onToggleMember(member.userId)}
        className={`w-full grid items-center px-4 py-3 text-left gap-3 ${canExpand ? 'cursor-pointer' : 'cursor-default'}`}
        style={{ gridTemplateColumns: '1fr 80px 110px' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 ${AVATAR_COLORS[idx % AVATAR_COLORS.length]}`}>
            {getInitials(member.firstName, member.lastName)}
          </div>
          <div className="min-w-0">
            <p className={`text-sm font-semibold ${theme.text} truncate`}>
              {isMe ? 'You' : `${member.firstName} ${member.lastName}`}
            </p>
            <p className={`text-xs ${theme.textSecondary}`}>
              {owesTo.length > 0
                ? `Owes ${owesTo.length} ${owesTo.length === 1 ? 'person' : 'people'}`
                : member.net > 0
                  ? 'Owed by others'
                  : 'All settled'}
            </p>
          </div>
        </div>

        <p className={`text-sm font-bold text-right ${member.net >= 0 ? 'text-green-500' : 'text-red-500'}`}>
          {member.net >= 0 ? '+' : ''}${member.net.toFixed(0)}
        </p>

        <div className="flex items-center justify-end gap-1.5">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${
            member.status === 'settled'
              ? 'bg-green-500/15 text-green-500'
              : 'bg-orange-500/15 text-orange-500'
          }`}>
            {member.status}
          </span>
          <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''} ${canExpand ? theme.textSecondary : 'opacity-0'}`} />
        </div>
      </button>

      {owesTo.length > 0 && expanded && (
        <div className={`border-t ${theme.borderSecondary}`}>
          {owesTo.map((o, i) => (
            <div
              key={i}
              className={`grid items-center px-4 py-2.5 gap-3 ${i < owesTo.length - 1 ? `border-b ${theme.borderSecondary}` : ''}`}
              style={{ gridTemplateColumns: '1fr 80px 110px' }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 flex justify-center flex-shrink-0">
                  <ArrowRight className="w-3.5 h-3.5 text-red-400" />
                </div>
                <p className={`text-sm ${theme.text} truncate`}>
                  {[o.firstName, o.lastName].filter(Boolean).join(' ')}
                </p>
              </div>
              <p className="text-sm font-semibold text-red-500 text-right">${o.amount.toFixed(0)}</p>
              <div className="flex justify-end">
                {isMe && o.creditTransactions?.length > 0 ? (
                  <button
                    onClick={() => onSettleUp({
                      creditorName: [o.firstName, o.lastName].filter(Boolean).join(' '),
                      amount: o.amount,
                      creditTransactions: o.creditTransactions,
                    })}
                    className={`flex-shrink-0 ${btn.base} ${btn.sm} ${theme.buttonPrimary}`}
                  >
                    Settle Up
                  </button>
                ) : <span />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * BalanceSummaryComponent
 *
 * Owns:
 *   - expandedMembers state (which member rows are drilled-down)
 *
 * Props:
 *   seasonFilter, uniqueYears, yearBalances    – from parent (data)
 *   expandedYears, onToggleYear                – from parent (year collapse controlled by ExpenseTab
 *                                                because loadYearBalances lives there)
 *   balanceMembers                             – flat list for season-filtered view
 *   loadingBalances, currentUserId             – display/identity
 *   onSettleUp                                 – opens SettleUpModal in parent
 *   theme
 */
const BalanceSummaryComponent = ({
  seasonFilter,
  uniqueYears,
  yearBalances,
  expandedYears,
  onToggleYear,
  balanceMembers,
  loadingBalances,
  currentUserId,
  onSettleUp,
  theme,
}) => {
  const [expandedMembers, setExpandedMembers] = useState(
    () => (currentUserId ? { [currentUserId]: true } : {})
  );

  const toggleMember = (userId) =>
    setExpandedMembers(p => ({ ...p, [userId]: !p[userId] }));

  return (
    <div className={`${theme.card} rounded-2xl border ${theme.border} overflow-hidden`}>
      <div className="px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet className={`w-4 h-4 ${theme.textSecondary}`} />
          <h3 className={`font-semibold ${theme.text}`}>Balance Summary</h3>
          {loadingBalances && <Loader className="w-3.5 h-3.5 text-blue-500 animate-spin" />}
        </div>
      </div>

      {/* "All" mode — year-grouped, each year collapsible */}
      {seasonFilter === null ? (
        uniqueYears.length === 0 ? (
          <div className="px-5 pb-5 text-center">
            <p className={`text-sm ${theme.textSecondary}`}>No balances yet. Add an expense to get started.</p>
          </div>
        ) : (
          <div>
            {uniqueYears.map(year => {
              const isExpanded = !!expandedYears[year];
              const ybData     = yearBalances[year];
              const loading    = ybData?.loading ?? false;
              const members    = ybData?.members ?? [];

              return (
                <div key={year} className={`border-t ${theme.borderSecondary}`}>
                  <button
                    type="button"
                    onClick={() => onToggleYear(year)}
                    className={`w-full flex items-center justify-between px-5 py-3 text-left transition-colors ${theme.cardHover}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${theme.text}`}>{year}</span>
                      {members.length > 0 && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${theme.backgroundSecondary} ${theme.textSecondary}`}>
                          {members.length} members
                        </span>
                      )}
                      {loading && <Loader className="w-3.5 h-3.5 text-blue-500 animate-spin" />}
                    </div>
                    <ChevronDown className={`w-4 h-4 ${theme.textSecondary} transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>

                  {isExpanded && (
                    loading ? (
                      <div className={`px-5 py-4 flex items-center gap-2 border-t ${theme.borderSecondary}`}>
                        <Loader className="w-4 h-4 text-blue-500 animate-spin" />
                        <p className={`text-sm ${theme.textSecondary}`}>Loading {year} balances…</p>
                      </div>
                    ) : members.length === 0 ? (
                      <div className={`px-5 py-3 border-t ${theme.borderSecondary}`}>
                        <p className={`text-sm ${theme.textSecondary}`}>No balances for {year}.</p>
                      </div>
                    ) : (
                      <div className={`border-t ${theme.borderSecondary} divide-y ${theme.borderSecondary}`}>
                        {members.map((member, idx) => (
                          <MemberRow
                            key={member.userId}
                            member={member}
                            idx={idx}
                            currentUserId={currentUserId}
                            expandedMembers={expandedMembers}
                            onToggleMember={toggleMember}
                            onSettleUp={onSettleUp}
                            theme={theme}
                          />
                        ))}
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* Season-filtered mode — flat member list */
        balanceMembers.length === 0 ? (
          <div className="px-5 pb-5 text-center">
            <p className={`text-sm ${theme.textSecondary}`}>No balances for this period.</p>
          </div>
        ) : (
          <div className={`border-t ${theme.borderSecondary} divide-y ${theme.borderSecondary}`}>
            {balanceMembers.map((member, idx) => (
              <MemberRow
                key={member.userId}
                member={member}
                idx={idx}
                currentUserId={currentUserId}
                expandedMembers={expandedMembers}
                onToggleMember={toggleMember}
                onSettleUp={onSettleUp}
                theme={theme}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default BalanceSummaryComponent;
